/**
 * Stable error codes of revenue cycles (ciclo de receita, ALC-5089) and their copy in both
 * languages. The API throws with this copy and the web renders it from here, so a code cannot
 * ship without pt-BR and en text: `REVENUE_CYCLE_ERROR_MESSAGES` is a total `Record`.
 * Classify errors by `code`, never by message text.
 */
export const REVENUE_CYCLE_ERROR_CODES = [
  'REVENUE_CYCLE_NOT_FOUND',
  'REVENUE_CYCLE_NAME_REQUIRED',
  'REVENUE_CYCLE_NAME_RESERVED',
  'REVENUE_CYCLE_NAME_TAKEN',
  'REVENUE_CYCLE_RECONCILED_REQUIRES_AGGREGATION',
  'REVENUE_CYCLE_AGGREGATION_REQUIRES_ACCUMULATED_LINE',
  'REVENUE_CYCLE_INACTIVE',
  'REVENUE_CYCLE_IN_USE',
  'REVENUE_CYCLE_CONTRACT_CLOSED',
  'AR_RECEIVE_FIRST_RETAINED_ISS_UNSUPPORTED',
  'AR_RECEIVE_FIRST_FOREIGN_CURRENCY_UNSUPPORTED',
  'AR_RECEIVE_FIRST_USAGE_CONTRACT_UNSUPPORTED',
  'AR_RECEIVE_FIRST_COUNTRY_UNSUPPORTED',
  'AR_RECEIVE_FIRST_FUNCTIONAL_CURRENCY_UNSUPPORTED',
  'AR_RECEIVE_FIRST_CANCEL_UNDO_RECEIPT_FIRST',
  'AR_RECEIVE_FIRST_CANCEL_REVERSAL_PERMISSION_REQUIRED',
  'AR_RECEIVE_FIRST_NF_CANCEL_PERIOD_CLOSED',
  'AR_RECEIVE_FIRST_NF_CANCEL_ISS_NOT_RECLASSIFIED',
  'AR_RECEIVE_FIRST_NF_CANCEL_RECEIVED_ABOVE_NET',
  'AR_EXTERNAL_NF_COUNTRY_UNSUPPORTED',
  'AR_EXTERNAL_NF_FOREIGN_CURRENCY_UNSUPPORTED',
  'AR_EXTERNAL_NF_RETAINED_ISS_UNSUPPORTED',
  'AR_EXTERNAL_NF_USAGE_CONTRACT_UNSUPPORTED',
  'AR_EXTERNAL_NF_MIXED_WITHHOLDING_UNSUPPORTED',
  'AR_EXTERNAL_NF_MIXED_RECEIVE_FIRST_UNSUPPORTED',
  'AR_EXTERNAL_NF_FIELDS_INCOMPLETE',
  'AR_EXTERNAL_NF_NOT_APPLICABLE',
  'AR_EXTERNAL_NF_INVOICE_NOT_LINKABLE',
  'AR_EXTERNAL_NF_NUMBER_CONFLICT',
  'AR_EXTERNAL_NF_RECLASSIFY_PERMISSION_REQUIRED',
  'AR_EXTERNAL_NF_ISS_PROVISION_NOT_FOUND',
  'AR_CONTRACT_AGGREGATE_NF_NOT_CONFIGURED',
  'AR_CONTRACT_AGGREGATE_NF_VALIDATION_FAILED',
  'AR_CONTRACT_AGGREGATE_NF_NOT_REQUESTED',
  'AR_CONTRACT_AGGREGATE_MIXED_SERVICES',
  'AR_CONTRACT_AGGREGATE_MIXED_TRANSACTION_UNSUPPORTED',
  'AR_CONTRACT_AGGREGATE_RETAINED_ISS_UNSUPPORTED',
  'AR_CONTRACT_AGGREGATE_TAX_SETUP_INCOMPLETE',
  'AR_CONTRACT_AGGREGATE_STALE',
  'AR_CONTRACT_AGGREGATE_NO_OPEN_PERIOD',
  'AR_CONTRACT_AGGREGATE_UNAVAILABLE',
  'AR_CONTRACT_AGGREGATE_OPEN_BELOW_WITHHOLDINGS',
  'AR_CONTRACT_AGGREGATE_MEMBERS_CHANGED',
  'AR_CONTRACT_AGGREGATE_FOREIGN_CURRENCY_UNSUPPORTED',
  'AR_NFSE_CHILD_BILLED_ON_AGGREGATE',
  'AR_ISS_RECLASS_NO_OPEN_PERIOD',
  'AR_ISS_RECLASS_DEDUCTION_ACCOUNT_UNRESOLVED',
  'AR_ISS_RECLASS_OWED_AMOUNT_MISSING',
  'AR_NFSE_CANCEL_SCOPE_CHANGED',
  'FISCAL_NOTE_CANCELLATION_FOR_REISSUE',
  'AR_AUTO_APPROVE_SETTING_PERMISSION_REQUIRED',
  'AR_AUTO_APPROVE_COMPETENCY_PERIOD_CLOSED',
] as const;

export type RevenueCycleErrorCode = (typeof REVENUE_CYCLE_ERROR_CODES)[number];

export const REVENUE_CYCLE_ERROR_MESSAGES: Readonly<
  Record<RevenueCycleErrorCode, { readonly en: string; readonly ptBR: string }>
> = {
  REVENUE_CYCLE_NOT_FOUND: {
    en: 'Revenue cycle not found.',
    ptBR: 'Ciclo de receita não encontrado.',
  },
  REVENUE_CYCLE_NAME_REQUIRED: {
    en: 'Give the revenue cycle a name.',
    ptBR: 'Informe o nome do ciclo de receita.',
  },
  REVENUE_CYCLE_NAME_RESERVED: {
    en: '"Padrão" is the name of the system cycle. Choose another name.',
    ptBR: '"Padrão" é o nome do ciclo do sistema. Escolha outro nome.',
  },
  REVENUE_CYCLE_NAME_TAKEN: {
    en: 'A revenue cycle with this name already exists.',
    ptBR: 'Já existe um ciclo de receita com este nome.',
  },
  REVENUE_CYCLE_RECONCILED_REQUIRES_AGGREGATION: {
    en: 'Billing only reconciled transactions requires billing accumulated lines in one invoice per period.',
    ptBR: 'Faturar só transações conciliadas exige faturar as linhas acumuladas em uma fatura por período.',
  },
  REVENUE_CYCLE_AGGREGATION_REQUIRES_ACCUMULATED_LINE: {
    en: 'This cycle bills accumulated lines in one invoice per period, but the contract has no line set to "Accumulated for the period".',
    ptBR: 'Este ciclo fatura as linhas acumuladas em uma fatura por período, mas o contrato não tem nenhuma linha "Acumulado do período".',
  },
  REVENUE_CYCLE_INACTIVE: {
    en: 'This revenue cycle is inactive and cannot be assigned.',
    ptBR: 'Este ciclo de receita está inativo e não pode ser atribuído.',
  },
  REVENUE_CYCLE_IN_USE: {
    en: 'This revenue cycle is used by contract templates, contracts or invoices. Deactivate it instead.',
    ptBR: 'Este ciclo de receita é usado por modelos de contrato, contratos ou faturas. Desative-o em vez de excluí-lo.',
  },
  REVENUE_CYCLE_CONTRACT_CLOSED: {
    en: 'The revenue cycle of a cancelled or expired contract cannot be changed.',
    ptBR: 'O ciclo de receita de um contrato cancelado ou expirado não pode ser alterado.',
  },
  AR_RECEIVE_FIRST_RETAINED_ISS_UNSUPPORTED: {
    en: 'This cycle does not support ISS withheld by the customer yet. Use the Padrão cycle for this contract.',
    ptBR: 'Este ciclo ainda não suporta ISS retido. Use o ciclo Padrão para este contrato.',
  },
  AR_RECEIVE_FIRST_FOREIGN_CURRENCY_UNSUPPORTED: {
    en: 'This cycle supports only invoices in the company currency. Use the Padrão cycle for this invoice.',
    ptBR: 'Este ciclo só aceita faturas na moeda da empresa. Use o ciclo Padrão para esta fatura.',
  },
  AR_RECEIVE_FIRST_USAGE_CONTRACT_UNSUPPORTED: {
    en: 'This cycle does not support usage lines recognized before billing yet. Use the Padrão cycle for this contract.',
    ptBR: 'Este ciclo ainda não suporta linhas de uso reconhecidas antes do faturamento. Use o ciclo Padrão para este contrato.',
  },
  AR_RECEIVE_FIRST_COUNTRY_UNSUPPORTED: {
    en: 'This cycle is available only for companies in Brazil. Use the Padrão cycle for this invoice.',
    ptBR: 'Este ciclo só está disponível para empresas no Brasil. Use o ciclo Padrão para esta fatura.',
  },
  AR_RECEIVE_FIRST_FUNCTIONAL_CURRENCY_UNSUPPORTED: {
    en: 'This cycle issues an NFS-e after the receipt, and an NFS-e needs the company books in reais (BRL). This company keeps its books in another currency, so this cycle cannot be used here.',
    ptBR: 'Este ciclo emite a NFS-e depois do recebimento, e a NFS-e exige os livros da empresa em reais (BRL). Esta empresa mantém os livros em outra moeda, então este ciclo não pode ser usado aqui.',
  },
  AR_RECEIVE_FIRST_CANCEL_UNDO_RECEIPT_FIRST: {
    en: 'This invoice has already been received, so it cannot be cancelled. Undo the receipt first, then cancel the invoice.',
    ptBR: 'Esta fatura já foi recebida, então não pode ser cancelada. Desfaça o recebimento primeiro e depois cancele a fatura.',
  },
  AR_RECEIVE_FIRST_CANCEL_REVERSAL_PERMISSION_REQUIRED: {
    en: 'Cancelling this invoice reverses its accounting entries, and you do not have permission to create reversals. Ask someone with that permission to cancel the invoice.',
    ptBR: 'Cancelar esta fatura estorna os lançamentos contábeis dela, e você não tem permissão para criar estornos. Peça a quem tem essa permissão para cancelar a fatura.',
  },
  AR_RECEIVE_FIRST_NF_CANCEL_PERIOD_CLOSED: {
    en: 'The accounting period of the cancellation date is closed, so the NF ISS entry cannot be reversed and the NF was not cancelled. Choose a date in an open period, or ask your accountant to reopen the period.',
    ptBR: 'O período contábil da data do cancelamento está fechado, então o lançamento do ISS da NF não pode ser estornado e a NF não foi cancelada. Escolha uma data em um período aberto ou peça ao seu contador para reabrir o período.',
  },
  AR_RECEIVE_FIRST_NF_CANCEL_ISS_NOT_RECLASSIFIED: {
    en: 'This invoice has already been received, and its ISS was booked when it was approved. Cancelling only the NF would leave that ISS payable for a cancelled NF. Undo the receipt first, then cancel the NF.',
    ptBR: 'Esta fatura já foi recebida, e o ISS dela foi contabilizado na aprovação. Cancelar só a NF deixaria esse ISS a recolher para uma NF cancelada. Desfaça o recebimento primeiro e depois cancele a NF.',
  },
  AR_RECEIVE_FIRST_NF_CANCEL_RECEIVED_ABOVE_NET: {
    en: 'The customer has already paid more than this invoice would be owed once the NF withholdings are put back as approved. Cancelling only the NF would leave the installments below what was received. Undo the receipt first, then cancel the NF.',
    ptBR: 'O cliente já pagou mais do que esta fatura ficaria devendo com as retenções da aprovação de volta. Cancelar só a NF deixaria as parcelas abaixo do que foi recebido. Desfaça o recebimento primeiro e depois cancele a NF.',
  },
  AR_EXTERNAL_NF_COUNTRY_UNSUPPORTED: {
    en: 'Services whose NF is issued outside Alchemy ERP are available only for companies in Brazil. Set the service to have its NF issued in Alchemy ERP, or bill it from a company in Brazil.',
    ptBR: 'Serviços com NF emitida fora do Alchemy ERP só estão disponíveis para empresas no Brasil. Configure o serviço para emitir a NF no Alchemy ERP ou fature por uma empresa no Brasil.',
  },
  AR_EXTERNAL_NF_FOREIGN_CURRENCY_UNSUPPORTED: {
    en: 'An invoice with services whose NF is issued outside Alchemy ERP must be in the company currency. Change the invoice currency to the company currency.',
    ptBR: 'Uma fatura com serviços de NF emitida fora do Alchemy ERP precisa estar na moeda da empresa. Altere a moeda da fatura para a moeda da empresa.',
  },
  AR_EXTERNAL_NF_RETAINED_ISS_UNSUPPORTED: {
    en: 'ISS withheld by the customer is not supported yet on an invoice whose NF is issued outside Alchemy ERP. Remove the ISS withholding from the invoice, or set the service to have its NF issued in Alchemy ERP.',
    ptBR: 'ISS retido ainda não é suportado em uma fatura com NF emitida fora do Alchemy ERP. Remova a retenção de ISS da fatura ou configure o serviço para emitir a NF no Alchemy ERP.',
  },
  AR_EXTERNAL_NF_USAGE_CONTRACT_UNSUPPORTED: {
    en: 'Usage already recognized before billing needs the external NF on this invoice. Inform the external NF number and issue date, then approve again.',
    ptBR: 'Uso já reconhecido antes do faturamento exige a NF externa nesta fatura. Informe o número e a data de emissão da NF externa e aprove novamente.',
  },
  AR_EXTERNAL_NF_MIXED_WITHHOLDING_UNSUPPORTED: {
    en: 'This invoice mixes services with NF issued in Alchemy ERP and outside it, and has withholdings. Split it into one invoice per NF issuer.',
    ptBR: 'Esta fatura mistura serviços com NF emitida no Alchemy ERP e fora dele, e tem retenções. Separe em uma fatura por emissor da NF.',
  },
  AR_EXTERNAL_NF_MIXED_RECEIVE_FIRST_UNSUPPORTED: {
    en: 'An invoice that mixes services with NF issued in Alchemy ERP and outside it cannot use a cycle that issues the NF after receipt. Use the Padrão cycle or split the invoice.',
    ptBR: 'Uma fatura que mistura serviços com NF emitida no Alchemy ERP e fora dele não pode usar um ciclo que emite a NF após o recebimento. Use o ciclo Padrão ou separe a fatura.',
  },
  AR_EXTERNAL_NF_FIELDS_INCOMPLETE: {
    en: 'Inform the external NF number and issue date together.',
    ptBR: 'Informe o número e a data de emissão da NF externa juntos.',
  },
  AR_EXTERNAL_NF_NOT_APPLICABLE: {
    en: "This invoice has no service whose NF is issued outside Alchemy ERP. Attach the NF on the invoice's fiscal note instead.",
    ptBR: 'Esta fatura não tem serviço com NF emitida fora do Alchemy ERP. Anexe a NF na nota fiscal da fatura.',
  },
  AR_EXTERNAL_NF_INVOICE_NOT_LINKABLE: {
    en: 'An external NF cannot be linked to a voided, replaced or out-of-date invoice. Link it to the current invoice for this billing.',
    ptBR: 'Não é possível vincular uma NF externa a uma fatura cancelada, substituída ou desatualizada. Vincule-a à fatura atual desta cobrança.',
  },
  AR_EXTERNAL_NF_NUMBER_CONFLICT: {
    en: 'This invoice already has a different external NF linked. Check the NF number; to replace it, void the invoice and issue a new one.',
    ptBR: 'Esta fatura já tem outra NF externa vinculada. Confira o número da NF; para trocá-la, cancele a fatura e emita uma nova.',
  },
  AR_EXTERNAL_NF_RECLASSIFY_PERMISSION_REQUIRED: {
    en: 'Linking this NF reclassifies the ISS already in the accounting entry of the invoice, and you do not have permission to reclassify invoices. Ask someone with that permission to link the NF.',
    ptBR: 'Vincular esta NF reclassifica o ISS que já está no lançamento contábil da fatura, e você não tem permissão para reclassificar faturas. Peça a quem tem essa permissão para vincular a NF.',
  },
  AR_EXTERNAL_NF_ISS_PROVISION_NOT_FOUND: {
    en: 'The ISS this invoice set aside until its external NF was linked could not be found in its accounting entry, so the NF was not linked. Ask your accountant to review the invoice entry.',
    ptBR: 'O ISS que esta fatura reservou até a NF externa ser vinculada não foi encontrado no lançamento contábil, então a NF não foi vinculada. Peça ao seu contador para revisar o lançamento da fatura.',
  },
  AR_CONTRACT_AGGREGATE_NF_NOT_CONFIGURED: {
    en: 'NFS-e issuing is not set up for this company, so the period invoice cannot be approved. Set up NFS-e issuing for the company, then approve again.',
    ptBR: 'A emissão de NFS-e não está configurada para esta empresa, então a fatura do período não pode ser aprovada. Configure a emissão de NFS-e da empresa e aprove novamente.',
  },
  AR_CONTRACT_AGGREGATE_NF_VALIDATION_FAILED: {
    en: "The NFS-e of this period invoice is missing required data. Fix the data shown on the invoice's NF, then approve again.",
    ptBR: 'Faltam dados obrigatórios na NFS-e desta fatura do período. Corrija os dados indicados na NF da fatura e aprove novamente.',
  },
  AR_CONTRACT_AGGREGATE_NF_NOT_REQUESTED: {
    en: "The NFS-e of this period invoice could not be requested now. Check the invoice's NF, then approve again.",
    ptBR: 'A NFS-e desta fatura do período não pôde ser solicitada agora. Confira a NF da fatura e aprove novamente.',
  },
  AR_CONTRACT_AGGREGATE_MIXED_SERVICES: {
    en: 'The accumulated lines of this contract use more than one service, and one NF covers one service. Use one service for the accumulated lines, or set the other lines to Individual.',
    ptBR: 'As linhas acumuladas deste contrato usam mais de um serviço, e uma NF cobre um serviço. Use um só serviço nas linhas acumuladas ou mude as outras para Individual.',
  },
  AR_CONTRACT_AGGREGATE_MIXED_TRANSACTION_UNSUPPORTED: {
    en: 'A transaction of this period has lines on this NF and lines billed another way (an NF issued outside Alchemy ERP or its own NF), and the period invoice cannot settle only part of it. Put those lines on separate transactions, then run billing again.',
    ptBR: 'Uma transação deste período tem linhas nesta NF e linhas faturadas de outra forma (NF emitida fora do Alchemy ERP ou NF própria), e a fatura do período não consegue quitar só parte dela. Coloque essas linhas em transações separadas e rode o faturamento novamente.',
  },
  AR_CONTRACT_AGGREGATE_RETAINED_ISS_UNSUPPORTED: {
    en: 'ISS withheld by the customer is not supported yet on a period invoice. Turn off ISS withholding on the service, or set the lines to Individual.',
    ptBR: 'ISS retido ainda não é suportado em uma fatura do período. Desative a retenção de ISS do serviço ou mude as linhas para Individual.',
  },
  AR_CONTRACT_AGGREGATE_TAX_SETUP_INCOMPLETE: {
    en: 'The taxes of this period invoice cannot be calculated: the company fiscal settings or the service tax settings are incomplete. Complete them, then run billing again.',
    ptBR: 'Os impostos desta fatura do período não podem ser calculados: as configurações fiscais da empresa ou do serviço estão incompletas. Complete-as e rode o faturamento novamente.',
  },
  AR_NFSE_CHILD_BILLED_ON_AGGREGATE: {
    en: 'This transaction is billed on its period invoice, so it cannot have an NFS-e of its own. Issue the NFS-e from the period invoice.',
    ptBR: 'Esta transação é faturada na fatura do período, então não pode ter uma NFS-e própria. Emita a NFS-e pela fatura do período.',
  },
  AR_CONTRACT_AGGREGATE_STALE: {
    en: 'The transactions of this period invoice changed since it was prepared. Run billing again, then approve the updated invoice.',
    ptBR: 'As transações desta fatura do período mudaram desde que ela foi preparada. Rode o faturamento novamente e aprove a fatura atualizada.',
  },
  AR_CONTRACT_AGGREGATE_NO_OPEN_PERIOD: {
    en: 'There is no open accounting period for today, so the withholdings of this period invoice cannot be posted. Open the period, then approve again.',
    ptBR: 'Não há período contábil aberto para hoje, então as retenções desta fatura do período não podem ser lançadas. Abra o período e aprove novamente.',
  },
  AR_CONTRACT_AGGREGATE_UNAVAILABLE: {
    en: 'Period invoices for this contract cannot be prepared right now. Try again later; if it persists, contact support.',
    ptBR: 'As faturas do período deste contrato não podem ser preparadas agora. Tente novamente mais tarde; se continuar, fale com o suporte.',
  },
  AR_CONTRACT_AGGREGATE_OPEN_BELOW_WITHHOLDINGS: {
    en: 'The customer still owes less on these transactions than the withholdings this NF would state, so the period invoice cannot be approved. Review the receipts of the period with your accountant, then run billing again.',
    ptBR: 'O cliente ainda deve, nestas transações, menos do que as retenções que esta NF informaria, então a fatura do período não pode ser aprovada. Revise os recebimentos do período com o seu contador e rode o faturamento novamente.',
  },
  AR_CONTRACT_AGGREGATE_MEMBERS_CHANGED: {
    en: 'Transactions joined or left this period invoice since it was prepared, so approving it would bill a different set. Run billing again, then approve the updated invoice.',
    ptBR: 'Transações entraram ou saíram desta fatura do período desde que ela foi preparada, então aprová-la faturaria outro conjunto. Rode o faturamento novamente e aprove a fatura atualizada.',
  },
  AR_CONTRACT_AGGREGATE_FOREIGN_CURRENCY_UNSUPPORTED: {
    en: 'A period invoice must be in the company currency, so this one cannot be approved. Change the contract currency to the company currency, then run billing again.',
    ptBR: 'Uma fatura do período precisa estar na moeda da empresa, então esta não pode ser aprovada. Altere a moeda do contrato para a moeda da empresa e rode o faturamento novamente.',
  },
  AR_ISS_RECLASS_NO_OPEN_PERIOD: {
    en: 'There is no open accounting period on the NF issue date, so the ISS of this invoice cannot be booked and the NF was not linked. Reopen the period or ask your accountant, then link the NF again.',
    ptBR: 'Não há período contábil aberto na data de emissão da NF, então o ISS desta fatura não pode ser lançado e a NF não foi vinculada. Reabra o período ou peça ao seu contador e vincule a NF novamente.',
  },
  AR_ISS_RECLASS_DEDUCTION_ACCOUNT_UNRESOLVED: {
    en: 'The ISS deduction account changed since this invoice was approved, so its ISS cannot be booked and the NF was not linked. Ask your accountant to review the ISS deduction mapping.',
    ptBR: 'A conta de dedução de ISS mudou desde a aprovação desta fatura, então o ISS não pode ser lançado e a NF não foi vinculada. Peça ao seu contador para revisar o mapeamento da dedução de ISS.',
  },
  AR_ISS_RECLASS_OWED_AMOUNT_MISSING: {
    en: 'This NF owes ISS but states no ISS amount, so the NF was not linked. Enter the ISS amount the NF states, then link it again.',
    ptBR: 'Esta NF tem ISS devido mas não informa o valor do ISS, então a NF não foi vinculada. Informe o valor de ISS da NF e vincule novamente.',
  },
  AR_NFSE_CANCEL_SCOPE_CHANGED: {
    en: 'Invoice status changed; re-run the cancellation preview.',
    ptBR: 'A situação da fatura mudou. Abra a prévia do cancelamento novamente.',
  },
  FISCAL_NOTE_CANCELLATION_FOR_REISSUE: {
    en: 'The NFS-e of this invoice is already being cancelled so the invoice can be issued again, not voided. Wait for that cancellation to finish, then cancel the invoice again.',
    ptBR: 'A NFS-e desta fatura já está sendo cancelada para reemissão, não para anular a fatura. Aguarde esse cancelamento terminar e cancele a fatura novamente.',
  },
  AR_AUTO_APPROVE_SETTING_PERMISSION_REQUIRED: {
    en: 'Only people who can approve invoices can change automatic approval. Ask someone with permission to approve invoices to change it.',
    ptBR: 'Só quem pode aprovar faturas pode alterar a aprovação automática. Peça a quem tem permissão para aprovar faturas que faça a alteração.',
  },
  AR_AUTO_APPROVE_COMPETENCY_PERIOD_CLOSED: {
    en: 'The competence date of this invoice is in a closed accounting period, so it was not approved automatically. Change the competence date and approve the invoice.',
    ptBR: 'A competência desta fatura está em um período contábil fechado, então ela não foi aprovada automaticamente. Altere a competência e aprove a fatura.',
  },
};
